import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { XIcon } from '../assets/X';

import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { userInfo } from '../store/selectors/user';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from './ui/form';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';

import { Gender, setUser } from '../store/slices/userSlice';
import { useUpdateUser } from '../modules/profile/services/update';
import { LoadingButton } from './ui/loadingButton';

type QrCardProps = {
  open: boolean;
  setOpen: (openQr: boolean) => void;
};

const genderSchema = z.object({
  gender: z.nativeEnum(Gender),
});
type GenderFormSchema = z.infer<typeof genderSchema>;

export const GenderDrawer = ({ open, setOpen }: QrCardProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { updateUser, isPending } = useUpdateUser();
  const user = useSelector(userInfo);
  const form = useForm<GenderFormSchema>({
    resolver: zodResolver(genderSchema),
    defaultValues: {
      gender: user.gender,
    },
  });

  // gender у нового пользователя пустой, и тогда сабмит уходил в никуда:
  // резолвер его отклонял молча, без единого сообщения на экране.
  const gender = form.watch('gender');

  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        open &&
        dialogRef.current &&
        !dialogRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen]);

  const handleSubmit = async (data: GenderFormSchema) => {
    await updateUser({
      gender: Number(data.gender),
    }).then(() => {
      dispatch(
        setUser({
          ...user,
          gender: data.gender,
        })
      );
      setOpen(false);
    });
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent
        ref={dialogRef}
        className="bg-fill-background rounded-t-[24px] px-4"
      >
        <div className="flex flex-row justify-between items-center pb-4">
          <DrawerTitle className="text-display-md">
            {t('gender.title')}
          </DrawerTitle>
          <div
            className="flex items-center justify-center h-6 w-6 rounded-full bg-button-secondary"
            onClick={() => setOpen(false)}
          >
            <XIcon className="text-text-icon-secondary" />
          </div>
        </div>

        <Form {...form}>
          <form
            className="flex flex-col space-y-5  pb-2 w-full"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <RadioGroup
              onValueChange={val => form.setValue('gender', val as Gender)}
              defaultValue={form.getValues('gender')}
              className="space-y-2"
            >
              <Label
                htmlFor="female"
                className="flex items-center gap-4 w-full h-[60px] bg-white rounded-2xl px-4 py-4"
              >
                <RadioGroupItem value={Gender.FEMALE} id="female" />
                <p className="text-body-base-regular">{t('gender.female')}</p>
              </Label>

              <Label
                htmlFor="male"
                className="flex items-center gap-4 w-full h-[60px] bg-white rounded-2xl px-4 py-4"
              >
                <RadioGroupItem value={Gender.MALE} id="male" />
                <div className="text-body-base-regular">{t('gender.male')}</div>
              </Label>
            </RadioGroup>

            <LoadingButton
              loading={isPending}
              disabled={!gender}
              type="submit"
              className="w-full bg-button-primary h-12 text-[16px] font-semibold font-inter leading-5 tracking-[0px] text-white rounded-xl"
            >
              {t('doneButton')}
            </LoadingButton>
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};
